import { useState } from "react";
import { arrayMove } from "@dnd-kit/sortable";

export const STAGING_AREA_ID = "staging";

export const usePhotoDragAndDrop = (initialContainers, setContainers) => {
  const [activePhoto, setActivePhoto] = useState(null);

  const findContainer = (id, containers) => {
    if (id === STAGING_AREA_ID || containers[id]) {
      return id;
    }
    return Object.keys(containers).find((key) =>
      containers[key].some((item) => item.photoId === id)
    );
  };

  const handleDragStart = (event) => {
    setActivePhoto(event.active?.data?.current?.photo ?? null);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActivePhoto(null);

    if (!over) return;

    // Handle quick drop zones
    if (over.data?.current?.type === 'quick-drop') {
      const targetReleaseId = over.data.current.targetReleaseId;

      // Preserve scroll position to prevent auto-scrolling
      const currentScrollY = window.scrollY;

      setContainers((containers) => {
        const sourceContainerId = findContainer(active.id, containers);

        if (!sourceContainerId) return containers;

        const sourceItems = containers[sourceContainerId];
        const activeIndex = sourceItems.findIndex(
          (item) => item.photoId === active.id
        );

        if (activeIndex === -1) return containers;

        const itemToMove = sourceItems[activeIndex];
        const newSourceItems = sourceItems.filter(
          (item) => item.photoId !== active.id
        );

        // Add to target container
        const targetItems = containers[targetReleaseId] || [];

        // Restore scroll position after state update
        setTimeout(() => {
          window.scrollTo(0, currentScrollY);
        }, 0);

        return {
          ...containers,
          [sourceContainerId]: newSourceItems,
          [targetReleaseId]: [...targetItems, itemToMove],
        };
      });
      return;
    }

    setContainers((containers) => {
      const activeContainerId = findContainer(active.id, containers);
      const overContainerId = findContainer(over.id, containers);

      if (!activeContainerId || !overContainerId) {
        return containers;
      }

      // Scenario 1: Reordering within the same container
      if (activeContainerId === overContainerId) {
        if (active.id === over.id) {
          return containers; // No change
        }
        const containerItems = containers[activeContainerId];
        const oldIndex = containerItems.findIndex(
          (item) => item.photoId === active.id
        );
        const newIndex = containerItems.findIndex(
          (item) => item.photoId === over.id
        );

        if (oldIndex === -1 || newIndex === -1) {
          return containers; // Item not found, should not happen
        }

        return {
          ...containers,
          [activeContainerId]: arrayMove(containerItems, oldIndex, newIndex),
        };
      }

      // Scenario 2: Moving between different containers
      const activeItems = containers[activeContainerId];
      const overItems = containers[overContainerId];
      const activeIndex = activeItems.findIndex(
        (item) => item.photoId === active.id
      );

      let overIndex = overItems.findIndex((item) => item.photoId === over.id);
      // If dropping on the container itself (not on an item), append to the end
      if (overIndex === -1) {
        overIndex = overItems.length;
      }

      const itemToMove = activeItems[activeIndex];
      const newActiveItems = activeItems.filter(
        (item) => item.photoId !== active.id
      );
      const newOverItems = [...overItems];
      newOverItems.splice(overIndex, 0, itemToMove);

      return {
        ...containers,
        [activeContainerId]: newActiveItems,
        [overContainerId]: newOverItems,
      };
    });
  };

  return { activePhoto, handleDragStart, handleDragEnd };
};
